import { BaseElement } from 'base';
import { bus } from 'bus';
import Swiper from 'swiper-core';
import { sectionFragment } from 'section-fragment';
import { isPlainClick } from 'is-plain-click';
import { MEDIA_VIEWER_TOPIC } from 'media-viewer-topic';
import { MediaFloor, MEDIA_PLAYING_TOPIC, mediaKey } from 'media-floor';

const REDUCE = '(prefers-reduced-motion: reduce)';
const TWO_UP = '(min-width: 1200px)';
const BUTTON_TAG = 'action-button';
const VARIANT_TOPIC = 'product:variant';
const SELECTION_TOPIC = 'product:selection';
const PAUSED = 'paused';
const PLAYING = 'playing';
const PLAY_STATES = [PAUSED, PLAYING];
const SLIDE_SPEED = 300;
const TAP_SLOP = 10;
const VIEWER_MEDIA = '.product-gallery__image, [data-video]';
const VIEWER_STRIP = [
  'class',
  'style',
  'loading',
  'fetchpriority',
  'data-video',
  'muted',
  'loop'
];
const SWIPE_FOCUSABLE =
  'input, select, option, textarea, button:not([data-media-open]), video, label';

class ProductGallery extends BaseElement {
  #swiper = null;
  #viewport = null;
  #live = null;
  #prev = null;
  #next = null;
  #wishlist = null;
  #thumbs = [];
  #videos = new Map();
  #playToggles = new Map();
  #intents = new Map();
  #failed = new Set();
  #rejected = new Set();
  #onScreen = false;
  #blocked = false;
  #floor = new MediaFloor();
  #reduceMq = null;
  #viewerIndex = -1;
  #press = null;
  #connecting = false;
  #inert = null;
  #focusToken = 0;
  #opener = null;

  onConnect() {
    this.#reduceMq = window.matchMedia(REDUCE);
    this.listen(this.#reduceMq, 'change', this.#onReduceChange);
    this.listen(this, 'keydown', this.#onKeydown);
    this.listen(this, 'click', this.#onClick);
    this.listen(this, 'pointerdown', this.#onPointerDown);
    this.listen(this, 'focusout', this.#onFocusOut);
    this.listen(this, 'play', this.#onVideoPlay, { capture: true });
    this.listen(this, 'pause', this.#onVideoPause, { capture: true });
    this.listen(this, 'ended', this.#onVideoPause, { capture: true });
    this.listen(this, 'error', this.#onVideoError, { capture: true });
    this.listen(document, 'visibilitychange', this.#sync);
    this.observe(
      new IntersectionObserver(this.#onVisibility.bind(this), { threshold: 0 }),
      this
    );
    this.cleanup(() => this.#unmount());
    this.#mount();
    this.#sync();
    this.subscribe(MEDIA_PLAYING_TOPIC, this.#onPlayingChanged);
    this.subscribe(VARIANT_TOPIC, this.#onVariant);
    this.subscribe(SELECTION_TOPIC, this.#onSelection);
    this.#connecting = true;
    this.subscribe(MEDIA_VIEWER_TOPIC, this.#onViewer);
    this.#connecting = false;
    if (this.dataset.sectionId)
      this.subscribeLive(VARIANT_TOPIC, this.#onRerender);
  }

  onDisconnect() {
    this.#pauseAll();
    this.#floor.release();
    this.#onScreen = false;
    this.#blocked = false;
  }

  #mount(intents = new Map()) {
    this.#viewport = this.q('[data-viewport]');
    this.#live = this.q('[data-live]');
    this.#prev = this.q(`${BUTTON_TAG}.product-gallery__arrow--prev`);
    this.#next = this.q(`${BUTTON_TAG}.product-gallery__arrow--next`);
    this.#wishlist = this.q(`${BUTTON_TAG}.product-gallery__wishlist-button`);
    this.#thumbs = this.qa('[data-thumb]');
    this.#bindArrows();
    this.#bindWishlist();
    this.#initSwiper();
    this.#collectMedia();
    for (const slide of this.#videos.keys()) {
      const intent = intents.get(slide.dataset.mediaId);
      if (intent) this.#intents.set(slide, intent);
    }
    this.#bindPlayToggles();
    this.#syncThumbs(0);
  }

  #unmount() {
    this.#pauseAll();
    this.#floor.release();
    this.#opener = null;
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
    this.#videos.clear();
    this.#playToggles.clear();
    this.#intents.clear();
    this.#failed.clear();
    this.#rejected.clear();
  }

  #collectMedia() {
    this.#videos.clear();
    this.#playToggles.clear();
    for (const video of this.qa('[data-video]')) {
      const slide = video.closest('[data-slide]');
      if (slide) this.#videos.set(slide, video);
    }
    for (const toggle of this.qa(`${BUTTON_TAG}.product-gallery__play`)) {
      const slide = toggle.closest('[data-slide]');
      if (slide) this.#playToggles.set(slide, toggle);
    }
  }

  #initSwiper() {
    if (!this.#viewport || this.#count() < 2) return;
    const mode = this.#loopMode();
    this.#swiper = new Swiper(this.#viewport, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: this.#slideGap(),
      loop: mode.loop,
      rewind: mode.rewind,
      loopAdditionalSlides: mode.extra,
      preventClicks: false,
      preventClicksPropagation: false,
      watchOverflow: true,
      watchSlidesProgress: true,
      focusableElements: SWIPE_FOCUSABLE,
      speed: this.#slideSpeed(),
      touchEventsTarget: 'container',
      on: {
        afterInit: instance => {
          this.#syncEdges(instance);
          this.#syncInert(instance);
          this.#syncThumbs(instance.realIndex);
        },
        progress: instance => this.#syncInert(instance),
        slideChange: instance => {
          this.#syncEdges(instance);
          this.#syncThumbs(instance.realIndex);
        },
        slideChangeTransitionEnd: instance => this.#announce(instance),
        transitionEnd: () => this.#sync(),
        fromEdge: instance => this.#syncEdges(instance),
        reachBeginning: instance => this.#syncEdges(instance),
        reachEnd: instance => this.#syncEdges(instance),
        lock: instance => this.#syncEdges(instance),
        unlock: instance => this.#syncEdges(instance),
        resize: instance => this.#onResize(instance)
      }
    });
  }

  #loopMode() {
    const count = this.#count();
    const perView = this.#perView();
    if (count >= perView + 2)
      return {
        loop: true,
        rewind: false,
        extra: count >= perView + 4 ? 1 : 0
      };
    if (count > perView) return { loop: false, rewind: true, extra: 0 };
    return { loop: false, rewind: false, extra: 0 };
  }

  #perView() {
    return window.matchMedia(TWO_UP).matches ? 2 : 1;
  }

  #onResize(instance) {
    const mode = this.#loopMode();
    if (instance.params.loop !== mode.loop) {
      const index = instance.realIndex;
      queueMicrotask(() => {
        if (!this.isConnected || instance !== this.#swiper) return;
        this.#swiper.destroy(true, true);
        this.#swiper = null;
        this.#initSwiper();
        this.#collectMedia();
        this.#bindPlayToggles();
        this.#goTo(index, 0);
        this.#sync();
      });
      return;
    }
    instance.params.rewind = mode.rewind;
    const gap = this.#slideGap();
    if (instance.params.spaceBetween !== gap) {
      instance.params.spaceBetween = gap;
      instance.update();
    }
    this.#syncEdges(instance);
    this.#syncInert(instance);
    this.#sync();
  }

  #syncInert(instance) {
    const visibleClass = instance.params.slideVisibleClass;
    const focused = document.activeElement;
    for (const slide of instance.slides) {
      const keep = focused ? slide.contains(focused) : false;
      slide.toggleAttribute(
        'inert',
        !keep && !slide.classList.contains(visibleClass)
      );
    }
  }

  #syncEdges(instance = this.#swiper) {
    if (!instance || instance.destroyed) return;
    const locked = instance.isLocked;
    const wraps = instance.params.loop || instance.params.rewind;
    this.#prev?.setDisabled?.(locked || (!wraps && instance.isBeginning));
    this.#next?.setDisabled?.(locked || (!wraps && instance.isEnd));
  }

  #syncThumbs(index) {
    if (!this.#thumbs.length) return;
    for (const thumb of this.#thumbs) {
      const current = Number(thumb.dataset.mediaIndex) === index;
      if (current) {
        thumb.setAttribute('aria-current', 'true');
        this.#scrollThumbIntoView(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }

  #scrollThumbIntoView(thumb) {
    const list = thumb.closest('.product-gallery__thumb-list');
    if (!list) return;
    const listRect = list.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const isRtl = getComputedStyle(list).direction === 'rtl';
    const maxScroll = list.scrollWidth - list.clientWidth;
    const physicalScrollLeft = isRtl
      ? maxScroll + list.scrollLeft
      : list.scrollLeft;
    const offsetWithinList =
      thumbRect.left - listRect.left + physicalScrollLeft;
    const physicalTarget =
      offsetWithinList - list.clientWidth / 2 + thumbRect.width / 2;
    const target = isRtl ? physicalTarget - maxScroll : physicalTarget;
    list.scrollTo({
      left: target,
      behavior: this.#reduceMq?.matches ? 'auto' : 'smooth'
    });
  }

  #announce(instance) {
    if (!this.#live || !this.contains(document.activeElement)) return;
    const slide = instance.slides[instance.activeIndex];
    this.#live.textContent = slide?.getAttribute('aria-label') ?? '';
  }

  #bindArrows() {
    const prev = this.#prev;
    const next = this.#next;
    if (!prev && !next) return;
    customElements.whenDefined(BUTTON_TAG).then(() => {
      if (!this.isConnected || prev !== this.#prev) return;
      prev?.run(() => this.#step(-1));
      next?.run(() => this.#step(1));
      this.#syncEdges();
    });
  }

  #step(direction) {
    const instance = this.#swiper;
    if (!instance || instance.isLocked) return;
    if (direction > 0) instance.slideNext();
    else instance.slidePrev();
  }

  #bindWishlist() {
    const toggle = this.#wishlist;
    if (!toggle) return;
    customElements.whenDefined(BUTTON_TAG).then(() => {
      if (!this.isConnected || toggle !== this.#wishlist) return;
      toggle.toggleIcon({ states: ['off', 'on'] });
    });
  }

  #goTo(index, speed) {
    const instance = this.#swiper;
    if (!instance || instance.destroyed) return;
    if (instance.params.loop) instance.slideToLoop(index, speed);
    else instance.slideTo(index, speed);
  }

  #onClick(event) {
    const opener = event.target.closest?.('[data-media-open]');
    if (opener && this.contains(opener)) {
      this.#openViewer(opener, event);
      return;
    }
    const tapped = this.#videoSlide(event);
    if (tapped) {
      this.#tapVideo(tapped, event);
      return;
    }
    const thumb = event.target.closest?.('[data-thumb]');
    if (!thumb || !this.contains(thumb)) return;
    event.preventDefault();
    this.#goTo(Number(thumb.dataset.mediaIndex) || 0);
  }

  #onPointerDown(event) {
    this.#press = event.isPrimary
      ? { x: event.clientX, y: event.clientY }
      : null;
  }

  #tapVideo(slide, event) {
    const instance = this.#swiper;
    if (instance && !instance.destroyed && !instance.allowClick) return;
    if (!isPlainClick(event) || this.#pressMoved(event)) return;
    this.#playToggles.get(slide)?.control?.click();
  }

  #pressMoved(event) {
    const press = this.#press;
    this.#press = null;
    if (!press || event.detail === 0) return false;
    return (
      Math.hypot(event.clientX - press.x, event.clientY - press.y) >= TAP_SLOP
    );
  }

  #openViewer(opener, event) {
    const viewer = this.dataset.viewer;
    if (!viewer || !isPlainClick(event) || this.#pressMoved(event)) return;
    const slide = opener.closest('[data-slide]');
    this.#opener = opener;
    bus.publish(MEDIA_VIEWER_TOPIC, {
      id: viewer,
      index: Number(slide?.dataset.mediaIndex) || 0,
      html: this.#viewerHtml()
    });
  }

  #viewerHtml() {
    const items = new Map();
    for (const node of this.qa(VIEWER_MEDIA)) {
      const slide = node.closest('[data-slide]');
      const index = Number(slide?.dataset.mediaIndex);
      if (!slide || !Number.isInteger(index)) continue;
      let item = items.get(index);
      if (!item) {
        item = { slide, media: [] };
        items.set(index, item);
      }
      if (item.slide === slide) item.media.push(node);
    }
    this.#inert ??= document.implementation.createHTMLDocument('');
    const doc = this.#inert;
    return [...items.entries()]
      .sort(([indexA], [indexB]) => indexA - indexB)
      .map(([index, { slide, media }]) => {
        const item = doc.createElement('div');
        item.dataset.mediaId = slide.dataset.mediaId ?? '';
        item.dataset.mediaIndex = String(index);
        if (slide.dataset.mediaType)
          item.dataset.mediaType = slide.dataset.mediaType;
        item.setAttribute('aria-label', slide.getAttribute('aria-label') ?? '');
        for (const node of media) {
          const copy = doc.importNode(node, true);
          for (const name of VIEWER_STRIP) copy.removeAttribute(name);
          item.append(copy);
        }
        return item.outerHTML;
      })
      .join('');
  }

  #onViewer(state) {
    const viewer = this.dataset.viewer;
    if (!viewer) return;
    if (state?.id === viewer) {
      if (this.#connecting && this.#refreshViewer(state)) return;
      this.#viewerIndex = Number(state.index) || 0;
      this.#showMedia(this.#viewerIndex);
      return;
    }
    if (this.#viewerIndex < 0) return;
    const index = this.#viewerIndex;
    const token = ++this.#focusToken;
    this.#viewerIndex = -1;
    requestAnimationFrame(() =>
      setTimeout(() => {
        if (token !== this.#focusToken || this.#viewerIndex >= 0) return;
        if (this.#focusMoved()) return;
        this.#focusOpener(index);
      })
    );
  }

  #refreshViewer(state) {
    const html = this.#viewerHtml();
    if (html === state.html) return false;
    bus.publish(MEDIA_VIEWER_TOPIC, html ? { ...state, html } : null);
    return true;
  }

  #showMedia(index) {
    const instance = this.#swiper;
    if (!instance || instance.destroyed || instance.realIndex === index) return;
    this.#goTo(index, 0);
  }

  #focusMoved() {
    const active = document.activeElement;
    return Boolean(
      active &&
      active !== document.body &&
      !this.contains(active) &&
      !active.closest('dialog:not([open])')
    );
  }

  #focusOpener(index) {
    if (!this.isConnected) return;
    const instance = this.#swiper;
    if (instance && !instance.destroyed) this.#syncInert(instance);
    const original = this.#opener;
    this.#opener = null;
    if (this.#canFocus(original)) {
      original.focus({ preventScroll: true });
      return;
    }
    const opener = this.qa('[data-media-open]').find(button => {
      const slide = button.closest('[data-slide]');
      return (
        Number(slide?.dataset.mediaIndex) === index &&
        !button.closest('[inert]')
      );
    });
    (opener ?? this.#viewport)?.focus({ preventScroll: true });
  }

  #canFocus(element) {
    return Boolean(
      element?.isConnected &&
      this.contains(element) &&
      !element.closest('[inert]')
    );
  }

  #onKeydown(event) {
    if (!this.#viewport || event.target !== this.#viewport) return;
    const key = event.key;
    if (key === 'Home' || key === 'End') {
      event.preventDefault();
      this.#goTo(key === 'Home' ? 0 : this.#count() - 1);
      return;
    }
    if (key !== 'ArrowLeft' && key !== 'ArrowRight') return;
    const instance = this.#swiper;
    if (!instance || instance.isLocked) return;
    event.preventDefault();
    const forward = instance.rtlTranslate
      ? key === 'ArrowLeft'
      : key === 'ArrowRight';
    this.#step(forward ? 1 : -1);
  }

  #onFocusOut() {
    queueMicrotask(() => {
      if (!this.isConnected) return;
      const instance = this.#swiper;
      if (instance && !instance.destroyed) this.#syncInert(instance);
    });
  }

  #bindPlayToggles() {
    if (!this.#playToggles.size) return;
    const pairs = [...this.#playToggles];
    customElements.whenDefined(BUTTON_TAG).then(() => {
      if (!this.isConnected) return;
      for (const [slide, toggle] of pairs) {
        toggle.toggleIcon({
          states: PLAY_STATES,
          onChange: () => this.#togglePlay(slide)
        });
        toggle.setIconState(this.#videos.get(slide)?.paused ? PAUSED : PLAYING);
      }
    });
  }

  #togglePlay(slide) {
    const video = this.#videos.get(slide);
    if (!video) return;
    if (!video.paused) {
      this.#intents.set(slide, PAUSED);
      video.pause();
      this.#sync();
      return;
    }
    for (const [other, intent] of this.#intents)
      if (other !== slide && intent === PLAYING) this.#intents.delete(other);
    for (const [other, media] of this.#videos)
      if (other !== slide && !media.paused) media.pause();
    this.#intents.set(slide, PLAYING);
    this.#blocked = false;
    this.#floor.claim(this.#keyOf(slide));
    this.#play(slide);
  }

  #play(slide) {
    const video = this.#videos.get(slide);
    if (!video?.paused) return;
    video.play().then(null, error => this.#onPlayRejected(slide, error));
    this.#render();
  }

  #onPlayRejected(slide, error) {
    if (!this.isConnected) return;
    if (error.name !== 'AbortError' && error.name !== 'NotAllowedError')
      console.error('[product-gallery] video play failed:', error);
    if (!this.#videos.get(slide)?.paused) return;
    if (error.name !== 'AbortError') this.#rejected.add(slide);
    this.#resetSlide(slide);
    this.#sync();
  }

  #wants(slide) {
    if (this.#failed.has(slide) || this.#rejected.has(slide)) return false;
    const intent = this.#intents.get(slide);
    if (intent) return intent === PLAYING;
    return !this.#reduceMq?.matches;
  }

  #inFrame() {
    const instance = this.#swiper;
    if (!instance || instance.destroyed) return this.qa('[data-slide]');
    const perView = this.#perView();
    const last = instance.params.loop
      ? instance.activeIndex
      : Math.max(0, instance.slides.length - perView);
    const start = Math.min(instance.activeIndex, last);
    return instance.slides.slice(start, start + perView);
  }

  #sync() {
    if (!this.#videos.size) return;
    const frame = this.#inFrame();
    const live = this.#onScreen && !document.hidden && !this.#blocked;
    const candidates = live
      ? frame.filter(slide => this.#videos.has(slide) && this.#wants(slide))
      : [];
    const chosen =
      candidates.find(slide => this.#intents.get(slide) === PLAYING) ??
      candidates[0];
    for (const [slide, video] of this.#videos)
      if (slide !== chosen && !video.paused) video.pause();
    if (chosen) this.#play(chosen);
    this.#render(frame);
  }

  #render(frame = this.#inFrame()) {
    const shown = this.#onScreen ? new Set(frame) : new Set();
    for (const [slide, video] of this.#videos)
      slide.toggleAttribute(
        'data-media-paused',
        shown.has(slide) ? this.#showsPlay(slide, video) : !this.#wants(slide)
      );
  }

  #showsPlay(slide, video) {
    if (!video.paused) return false;
    if (slide.hasAttribute('data-media-started')) return true;
    return !this.#wants(slide);
  }

  #videoSlide(event) {
    const video = event.target.closest?.('[data-video]');
    const slide = video?.closest('[data-slide]');
    return slide && this.#videos.get(slide) === video ? slide : null;
  }

  #onVideoPlay(event) {
    const slide = this.#videoSlide(event);
    if (!slide) return;
    slide.setAttribute('data-media-started', '');
    this.#failed.delete(slide);
    this.#rejected.delete(slide);
    this.#playToggles.get(slide)?.setIconState?.(PLAYING);
    if (!event.target.paused && !this.#blocked)
      this.#floor.claim(this.#keyOf(slide));
    this.#render();
  }

  #onVideoPause(event) {
    const slide = this.#videoSlide(event);
    if (!slide) return;
    this.#resetSlide(slide);
    this.#render();
  }

  #onVideoError(event) {
    const slide = this.#videoSlide(event);
    if (!slide) return;
    if (
      event.target.tagName === 'SOURCE' &&
      this.#videos.get(slide)?.networkState !==
        HTMLMediaElement.NETWORK_NO_SOURCE
    ) {
      return;
    }
    console.error(
      '[product-gallery] media failed to load:',
      event.target.currentSrc || event.target.src
    );
    slide.removeAttribute('data-media-started');
    this.#failed.add(slide);
    this.#resetSlide(slide);
    this.#sync();
  }

  #resetSlide(slide) {
    this.#playToggles.get(slide)?.setIconState?.(PAUSED);
    this.#floor.release(this.#keyOf(slide));
  }

  #onPlayingChanged(key) {
    const slides = [...this.#videos.keys()];
    const wasBlocked = this.#blocked;
    this.#blocked =
      Boolean(key) && !slides.some(slide => this.#keyOf(slide) === key);
    if (!key) {
      if (wasBlocked) this.#sync();
      return;
    }
    for (const [slide, video] of this.#videos) {
      if (this.#keyOf(slide) === key) continue;
      if (!video.paused) video.pause();
    }
    this.#render();
  }

  #onVisibility(entries) {
    this.#onScreen = entries.at(-1).isIntersecting;
    this.#sync();
  }

  #pauseAll() {
    for (const video of this.#videos.values()) if (!video.paused) video.pause();
  }

  #keyOf(slide) {
    return mediaKey(this.id, slide.dataset.mediaId);
  }

  #onVariant(state) {
    if (!state) return;
    const owner = this.dataset.productId;
    if (owner && String(state.productId) !== owner) return;
    const index = this.#indexOfState(state);
    if (index < 0 || this.#swiper?.realIndex === index) return;
    this.#goTo(index);
  }

  #indexOfState(state) {
    let index = -1;
    if (state?.mediaId != null) index = this.#indexOfMedia(state.mediaId);
    if (index < 0 && state?.variantId != null)
      index = this.#indexOfVariant(state.variantId);
    return index;
  }

  #onSelection(state) {
    if (!state) return;
    const owner = this.dataset.productId;
    if (owner && String(state.productId) !== owner) return;
    const preOrder = state.availability === 'pre-order';
    for (const label of this.qa('[data-product-badges-set]'))
      label.hidden =
        (label.dataset.productBadgesSet === 'pre-order') !== preOrder;
  }

  #onRerender(state) {
    const fresh = sectionFragment(
      state?.sections,
      this.dataset.sectionId,
      this.id
    );
    if (!fresh) return;
    const viewerOpen = this.#viewerIndex >= 0;
    if (viewerOpen) bus.publish(MEDIA_VIEWER_TOPIC, null);
    this.#focusToken++;
    const hadFocus = viewerOpen || this.contains(document.activeElement);
    const intents = new Map(
      [...this.#intents].map(([slide, intent]) => [
        slide.dataset.mediaId,
        intent
      ])
    );
    this.#unmount();
    this.replaceChildren(...fresh.childNodes);
    this.dataset.count = fresh.dataset.count ?? '0';
    if (fresh.dataset.productId)
      this.dataset.productId = fresh.dataset.productId;
    this.#mount(intents);
    this.#goTo(Math.max(this.#indexOfState(state), 0), 0);
    this.#sync();
    if (hadFocus) this.#viewport?.focus({ preventScroll: true });
  }

  #indexOfMedia(mediaId) {
    const slide = this.qa('[data-slide]').find(
      item => item.dataset.mediaId === String(mediaId)
    );
    return slide ? Number(slide.dataset.mediaIndex) : -1;
  }

  #indexOfVariant(variantId) {
    const id = String(variantId);
    const slide = this.qa('[data-slide]').find(item =>
      item.dataset.variantIds?.split(',').includes(id)
    );
    return slide ? Number(slide.dataset.mediaIndex) : -1;
  }

  #count() {
    return Number(this.dataset.count) || 0;
  }

  #slideSpeed() {
    return this.#reduceMq?.matches ? 0 : SLIDE_SPEED;
  }

  #onReduceChange() {
    if (this.#swiper) this.#swiper.params.speed = this.#slideSpeed();
    this.#sync();
  }

  #slideGap() {
    const bleed =
      parseFloat(getComputedStyle(this.#viewport).paddingInlineEnd) || 0;
    return Math.max(this.#readLength('--product-gallery-gap'), bleed);
  }

  #readLength(name) {
    return parseFloat(getComputedStyle(this).getPropertyValue(name)) || 0;
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
